import { useEffect, useState } from "react";
import { api, apiError } from "../lib/api";
import { cachedGet } from "../lib/offline";
import { MicroCheckEditor } from "../components/MicroCheckEditor";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import {
  SunHorizon, Package, Star, WarningOctagon, Sparkle, Megaphone,
  GraduationCap, CheckCircle, XCircle, ArrowClockwise, Swap, CalendarCheck,
} from "@phosphor-icons/react";

const alertVisual = (a) => {
  const c = (a.category || a.type || "").toLowerCase();
  if (c.includes("urgent") || c.includes("allergy")) return { Icon: WarningOctagon, tone: "border-l-red-500 bg-red-50/50" };
  if (c.includes("vip")) return { Icon: Star, tone: "border-l-slate-900 bg-slate-50" };
  if (c.includes("focus")) return { Icon: Sparkle, tone: "border-l-emerald-500 bg-emerald-50/50" };
  if (c.includes("stock")) return { Icon: Package, tone: "border-l-amber-400 bg-amber-50/50" };
  return { Icon: Megaphone, tone: "border-l-sky-500 bg-sky-50/50" };
};

export default function BriefingPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => cachedGet("/briefing/today", "briefing")
    .then((r) => { setData(r.data); setResult(r.data.result); }).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async () => {
    setBusy(true);
    try {
      const payload = { answers: Object.entries(answers).map(([qid, idx]) => ({ question_id: qid, answer_index: idx })) };
      const { data: res } = await api.post("/briefing/submit", payload);
      setResult(res);
      res.passed ? toast.success("You're shift-ready. Status: GREEN") : toast.error("Micro-check failed. Status: RED — retake required");
    } catch (e) {
      if (!e.response) toast.error("The micro-check needs a connection — try again when back online");
      else toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const retake = () => { setResult(null); setAnswers({}); };

  if (!data) return <p className="text-slate-400">Loading briefing…</p>;

  return (
    <div className="space-y-8 rise-in max-w-4xl mx-auto" data-testid="briefing-page">
      <div className="flex items-center gap-4">
        <div className="h-14 w-14 rounded-2xl bg-slate-900 flex items-center justify-center">
          <SunHorizon size={30} weight="duotone" className="text-amber-400" />
        </div>
        <div>
          <p className="text-sm uppercase tracking-[0.2em] font-bold text-slate-400">Pre-Shift Gate · {data.date}</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight">90-Second Briefing</h1>
        </div>
      </div>

      {result && (
        <div data-testid="briefing-result-banner"
          className={`rounded-2xl p-8 border-l-4 ${result.passed ? "bg-emerald-50 border border-emerald-200 border-l-emerald-500" : "bg-red-50 border border-red-200 border-l-red-500"}`}>
          <div className="flex items-center gap-4">
            {result.passed
              ? <CheckCircle size={44} weight="fill" className="text-emerald-500" />
              : <XCircle size={44} weight="fill" className="text-red-500" />}
            <div className="flex-1">
              <p className={`font-heading text-2xl font-black ${result.passed ? "text-emerald-700" : "text-red-700"}`}>
                {result.passed ? "SHIFT READY — GREEN" : "NON-COMPLIANT — RED"}
              </p>
              <p className="text-sm text-slate-600 mt-1">Micro-check score: {result.correct}/{result.total}. {result.passed ? "Your manager sees you as ready." : "Review the briefing below and retake."}</p>
              {result.passed && (
                <p data-testid="attendance-logged-note" className="text-xs font-bold text-emerald-700 mt-2 flex items-center gap-1.5">
                  <CalendarCheck size={16} weight="duotone" /> Attendance logged for {result.date} — counts toward your scorecard
                </p>
              )}
            </div>
            {!result.passed && (
              <button data-testid="briefing-retake-button" onClick={retake}
                className="h-14 px-6 rounded-full bg-slate-900 text-white font-heading font-bold flex items-center gap-2 tap-press">
                <ArrowClockwise size={20} /> Retake
              </button>
            )}
          </div>
        </div>
      )}

      <section>
        <h2 className="font-heading text-xl font-bold mb-4">Today's alerts</h2>
        <div className="grid sm:grid-cols-2 gap-4" data-testid="briefing-alerts">
          {data.alerts.map((a, i) => {
            const { Icon, tone } = alertVisual(a);
            return (
              <div key={a.id || i} className={`p-6 rounded-xl border border-slate-200 border-l-4 flex gap-4 ${tone}`}>
                <Icon size={26} weight="duotone" className="shrink-0 text-slate-700" />
                <div className="flex-1">
                  {(a.category || a.type) && (
                    <span className="inline-block text-[10px] uppercase tracking-wider font-bold text-slate-500 bg-white border border-slate-200 px-2 py-0.5 rounded-full mb-1.5">{a.category || a.type}</span>
                  )}
                  <p className="text-base font-semibold leading-relaxed">{a.label}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {data.handover && (data.handover.summary || data.handover.pending_tasks?.length > 0 || data.handover.low_stock?.length > 0 || data.handover.equipment_issues) && (
        <section data-testid="briefing-handover">
          <h2 className="font-heading text-xl font-bold mb-4 flex items-center gap-2">
            <Swap size={24} weight="duotone" className="text-amber-600" /> Last shift highlights · {data.handover.shift_label} ({data.handover.created_by_name})
          </h2>
          <div className="bg-white border border-slate-200 rounded-2xl p-6 border-l-4 border-l-amber-400 space-y-3">
            {data.handover.summary && (
              <p data-testid="briefing-handover-summary" className="text-base text-slate-700 leading-relaxed italic">"{data.handover.summary}"</p>
            )}
            {data.handover.pending_tasks?.map((t, i) => (
              <p key={i} className="text-base font-semibold">→ {t}</p>
            ))}
            {data.handover.low_stock?.length > 0 && (
              <div className="flex flex-wrap gap-2 pt-1">
                {data.handover.low_stock.map((s, i) => (
                  <span key={i} className="text-xs font-bold bg-amber-50 text-amber-700 border border-amber-200 px-3 py-1.5 rounded-full">Low stock: {s}</span>
                ))}
              </div>
            )}
            {data.handover.equipment_issues && (
              <p className="text-sm text-slate-600"><span className="font-bold">Equipment:</span> {data.handover.equipment_issues}</p>
            )}
          </div>
        </section>
      )}

      {data.training.length > 0 && (
        <section>
          <h2 className="font-heading text-xl font-bold mb-4 flex items-center gap-2">
            <GraduationCap size={24} weight="duotone" className="text-emerald-600" /> Assigned from guest feedback
          </h2>
          <div className="space-y-3" data-testid="briefing-training">
            {data.training.map((t, i) => (
              <div key={i} className="p-5 rounded-xl bg-white border border-slate-200 border-l-4 border-l-amber-400">
                <p className="text-xs uppercase tracking-[0.15em] font-bold text-amber-600">{t.complaint_type} · via {t.source}</p>
                <p className="text-base font-semibold mt-1">{t.training_action}</p>
                {t.action_pointers?.length > 0 && (
                  <ul className="mt-2 space-y-1" data-testid={`briefing-pointers-${i}`}>
                    {t.action_pointers.map((p, pi) => (
                      <li key={pi} className="text-sm text-slate-600 font-medium flex gap-2">
                        <span className="text-emerald-600 font-black">•</span> {p}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {["admin", "manager"].includes(user.role) && <MicroCheckEditor />}

      {!result && data.questions.length > 0 && (
        <section className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8">
          <h2 className="font-heading text-xl font-bold">Micro-check</h2>
          <p className="text-sm text-slate-500 mt-1 mb-6">Answer both correctly to go green.</p>
          <div className="space-y-8" data-testid="briefing-quiz">
            {data.questions.map((q, qi) => (
              <div key={q.id}>
                <p className="text-xs uppercase tracking-[0.15em] font-bold text-slate-400">Q{qi + 1} · from "{q.sop_title}"</p>
                <p className="font-heading font-bold text-lg mt-2">{q.question}</p>
                <div className="grid sm:grid-cols-2 gap-3 mt-4">
                  {q.options.map((o, oi) => (
                    <button key={oi} data-testid={`quiz-q${qi}-option-${oi}`}
                      onClick={() => setAnswers({ ...answers, [q.id]: oi })}
                      className={`min-h-16 px-5 py-4 rounded-xl border-2 text-left text-base font-semibold tap-press ${
                        answers[q.id] === oi ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white hover:border-slate-400"}`}>
                      {o}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <button data-testid="briefing-submit-button" onClick={submit}
            disabled={busy || Object.keys(answers).length < data.questions.length}
            className="mt-8 w-full h-16 rounded-full bg-emerald-600 text-white font-heading font-bold text-lg tap-press hover:bg-emerald-700 disabled:opacity-40">
            {busy ? "Checking…" : "Submit & start shift"}
          </button>
        </section>
      )}
    </div>
  );
}
